/**
 * DailyBlast chart-overlay wrapper — delegates to the canonical
 * Pine-exact engine at ./dailyBlastEngine.ts.
 *
 * Per v3 plan item 3: emits ONLY candleColors + markers.
 * All other visuals (envelope, CPR, pivots, VWAP, etc.) are driven by
 * independent indicator toggles, not by the strategy.
 */
import { CandleRow } from '../../components/charts/TradingChart';
import { ChartStrategy, StrategyOverlayResult, CandleColor, ChartMarker } from './types';
import { runDailyBlast, FourState, CandleType } from './dailyBlastEngine';

const STATE_COLORS: Record<FourState, string> = {
  dark_green:  '#00C853',   // strong bullish
  light_green: '#B9F6CA',   // weakening bearish (prepare long)
  dark_red:    '#D50000',   // strong bearish
  light_red:   '#FF8A80',   // weakening bullish (prepare short)
};

// R146 — uniform entry-marker palette (matches the RockBand benchmark).
const MARK_BUY = '#00C853';
const MARK_SHORT = '#D50000';

export interface DailyBlastOverlayOptions {
  candleType?: CandleType;
  combinedUpper?: (number | null)[];
  combinedLower?: (number | null)[];
  combinedMid?: (number | null)[];
}

export function computeDailyBlastOverlay(
  candles: CandleRow[],
  opts: DailyBlastOverlayOptions = {},
): StrategyOverlayResult {
  if (candles.length < 18) return { candleColors: [], markers: [] };

  const out = runDailyBlast(candles, {
    candleType: opts.candleType ?? 'HA',
    combinedUpper: opts.combinedUpper,
    combinedLower: opts.combinedLower,
    combinedMid: opts.combinedMid,
  });

  const candleColors: CandleColor[] = [];
  for (let i = 0; i < candles.length; i++) {
    const s = out.states[i];
    if (!s) continue;
    const color = STATE_COLORS[s];
    candleColors.push({ time: candles[i].time, upColor: color, downColor: color });
  }

  // R146 — uniform marker format (RockBand benchmark): B / S arrows, size 2.
  // Strength is conveyed in the crosshair tooltip, not on the marker.
  const markers: ChartMarker[] = out.confirmedSignals.map(sig => sig.action === 'BUY'
    ? { time: sig.time, position: 'belowBar', text: 'B', color: MARK_BUY, shape: 'arrowUp', size: 2 }
    : { time: sig.time, position: 'aboveBar', text: 'S', color: MARK_SHORT, shape: 'arrowDown', size: 2 });

  // Per-bar signal + running position for the unified crosshair tooltip.
  const signalsByTime: NonNullable<StrategyOverlayResult['signalsByTime']> = new Map();
  for (const sig of out.confirmedSignals) {
    signalsByTime.set(sig.time, { signal: sig.action, strength: sig.strength, pos: sig.action === 'BUY' ? 'LONG' : 'SHORT' });
  }
  const sigActionByTime = new Map(out.confirmedSignals.map(s => [s.time, s.action]));
  const positionByTime: NonNullable<StrategyOverlayResult['positionByTime']> = new Map();
  let pos: 'LONG' | 'SHORT' | 'FLAT' = 'FLAT';
  for (const c of candles) {
    const a = sigActionByTime.get(c.time);
    if (a === 'BUY') pos = 'LONG'; else if (a === 'SHORT') pos = 'SHORT';
    positionByTime.set(c.time, pos);
  }

  return { candleColors, markers, signalsByTime, positionByTime };
}

export const dailyBlastStrategy: ChartStrategy = {
  id: 'dailyblast',
  name: 'DailyBlast',
  computeOverlays(candles: CandleRow[]): StrategyOverlayResult {
    return computeDailyBlastOverlay(candles);
  },
};
